import * as Sentry from '@sentry/nuxt'
import { useClient } from '~/composables/api/supabase/client'
import { useGetJwt } from '~/composables/api/supabase/get-jwt'
import { BACK_TO_DASHBOARD_ID, IMPERSONATING } from '~/utils/consts/cookie-keys'

const SESSION_REFRESH_THRESHOLD_SECONDS = 120

export default defineNuxtRouteMiddleware(async (to, from) => {
   const localePath = useLocalePath()
   const client = useClient()
   const session = useSupabaseSession()
   const jwt = useGetJwt()
   const runtimeConfig = useRuntimeConfig()

   const impersonating = useCookie<boolean>(IMPERSONATING)
   const backToDashboardID = useCookie<string>(BACK_TO_DASHBOARD_ID)

   const inSafeRoutes =
      to.path === localePath('/auth/sign-in') ||
      to.path === localePath('/auth/sign-up') ||
      to.path === localePath('/auth/sign-out') ||
      to.path === localePath('/auth/reset-password')

   const inSignOutRoute = to.path === localePath('/auth/sign-out')
   const inVerifyEmailRoute = to.path === localePath('/auth/verify-email')
   const inRedirectRoute =
      to.path === localePath('/auth/redirect') || to.path === localePath('/auth/reset-password')

   const expiresAt = session.value?.expires_at
   const isFresh =
      !!expiresAt && expiresAt - Math.floor(Date.now() / 1000) > SESSION_REFRESH_THRESHOLD_SECONDS

   if (!isFresh) {
      try {
         await client.auth.refreshSession()
      } catch (e) {
         if (runtimeConfig.public.mode === 'development') {
            console.error('[middleware/auth] session-refresh-failed', e)
         } else {
            Sentry.captureException(e, {
               extra: { reason: 'session-refresh-failed', path: to.path },
            })
         }
      }
   }

   if (inSignOutRoute) return

   if (jwt.value?.user_details?.is_email_verified) {
      if (!inRedirectRoute) {
         return navigateTo(localePath('/auth/redirect'), {
            redirectCode: 302,
         })
      }
   } else if (session.value) {
      impersonating.value = false
      backToDashboardID.value = ''

      if (!inVerifyEmailRoute && to.path !== localePath('/auth/reset-password')) {
         return navigateTo(localePath('/auth/verify-email'), { redirectCode: 302 })
      }
   } else {
      impersonating.value = false
      backToDashboardID.value = ''

      if (!inSafeRoutes) {
         return navigateTo(localePath('/auth/sign-in'), { redirectCode: 302 })
      }
   }
})
